/**
 * @module features/profile/pages/ProfilePage
 * @description Cognitive multi-device Profile & Security page optimized for Desktop, Tablet, and Mobile.
 * Features an identity summary hero with 3 quick status tiles, segmented cognitive tabs
 * (General, Password, 2FA & Passkeys), zero layout shift, and 60fps micro-transitions.
 */

import * as React from "react";
import {
  User,
  Lock,
  Fingerprint,
  Shield,
  ShieldCheck,
  AlertCircle,
  RefreshCw,
  Copy,
  Check,
  ShieldAlert,
} from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { UserAvatar } from "@/features/users";
import { ProfileEditForm } from "../components/ProfileEditForm";
import { TotpSetupModal } from "../components/TotpSetupModal";
import { PasskeyDeviceList } from "../components/PasskeyDeviceList";
import { useProfile } from "../api/profile-api";

type ProfileTab = "general" | "password" | "security";

export function ProfilePage() {
  const { data: profile, isLoading, isError, error, refetch } = useProfile();
  const [activeTab, setActiveTab] = React.useState<ProfileTab>("general");
  const [isTotpModalOpen, setIsTotpModalOpen] = React.useState(false);
  const [copiedId, setCopiedId] = React.useState(false);

  const handleCopyId = () => {
    if (!profile?.id) return;
    try {
      navigator.clipboard.writeText(profile.id);
      setCopiedId(true);
      setTimeout(() => setCopiedId(false), 2000);
    } catch {
      // Ignore in restricted environments
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-5 animate-pulse">
        {/* Skeleton Hero */}
        <div className="p-5 rounded-2xl border border-border/80 dark:border-zinc-800 bg-card dark:bg-zinc-950 space-y-4">
          <div className="flex items-center gap-4">
            <Skeleton className="h-16 w-16 rounded-full" />
            <div className="space-y-2 flex-1">
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-4 w-64" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3 pt-2">
            <Skeleton className="h-14 rounded-xl" />
            <Skeleton className="h-14 rounded-xl" />
            <Skeleton className="h-14 rounded-xl" />
          </div>
        </div>
        {/* Skeleton Tabs */}
        <Skeleton className="h-10 w-full sm:w-80 rounded-xl" />
        <Skeleton className="h-72 w-full rounded-2xl" />
      </div>
    );
  }

  if (isError || !profile) {
    return (
      <div className="rounded-2xl border border-destructive/20 bg-destructive/10 p-8 text-center text-destructive">
        <AlertCircle className="mx-auto h-8 w-8" />
        <h2 className="mt-3 font-semibold text-base">Failed to load profile</h2>
        <p className="mt-1 text-xs text-destructive/80">
          {(error as any)?.errorDetails ||
            (error as any)?.message ||
            "Unable to communicate with the identity gateway."}
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          className="mt-4 bg-card text-foreground"
        >
          <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
          Retry
        </Button>
      </div>
    );
  }

  const maxPasskeys = profile.max_passkeys || 5;
  const passkeyCount = profile.passkeys?.length || 0;

  const tabs = [
    { id: "general" as ProfileTab, label: "General", icon: User },
    { id: "password" as ProfileTab, label: "Password", icon: Lock },
    {
      id: "security" as ProfileTab,
      label: "2FA",
      icon: Fingerprint,
      badge: `${passkeyCount}/${maxPasskeys}`,
    },
  ];

  return (
    <div className="space-y-5">
      {/* ------------------------------------------------------------- */}
      {/* Cognitive Profile Hero: Identity & Real-Time Security Health  */}
      {/* ------------------------------------------------------------- */}
      <div className="rounded-2xl border border-border/80 dark:border-zinc-800 bg-card dark:bg-zinc-950 p-4 sm:p-5 shadow-xs">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Left: User Avatar & Identification Details */}
          <div className="flex items-center gap-3.5 min-w-0">
            <div className="relative shrink-0">
              <UserAvatar
                name={profile.name}
                avatarUrl={profile.avatar_url}
                size="lg"
              />
              <span
                className="absolute bottom-0 right-0 h-3 w-3 rounded-full bg-emerald-500 ring-2 ring-card dark:ring-zinc-950"
                title="Online"
              />
            </div>

            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-lg sm:text-xl font-bold tracking-tight text-foreground truncate">
                  {profile.name}
                </h1>
                <span
                  className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${profile.role === "super_admin"
                    ? "bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-950 border-transparent shadow-xs"
                    : "bg-purple-50 dark:bg-purple-950/60 text-purple-700 dark:text-purple-400 border-purple-200 dark:border-purple-800/60"
                    }`}
                >
                  {profile.role === "super_admin" ? "Super Admin" : "Client Portal"}
                </span>
              </div>

              <p className="text-xs text-muted-foreground font-mono truncate mt-0.5">
                {profile.email_id}
              </p>

              <div className="flex items-center gap-2 text-[11px] font-mono mt-1">
                <button
                  type="button"
                  onClick={handleCopyId}
                  className="flex items-center gap-1.5 hover:text-foreground transition-colors group select-all"
                  title="Click to copy Account ULID"
                >
                  <span className="font-semibold text-foreground">ID:</span>
                  <span className="text-muted-foreground group-hover:text-foreground transition-colors">{profile.id}</span>
                  {copiedId ? (
                    <Check className="h-3 w-3 text-foreground shrink-0" />
                  ) : (
                    <Copy className="h-3 w-3 opacity-60 group-hover:opacity-100 shrink-0" />
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Right: 3 Quick-Stat Tiles shifted to top-right on desktop */}
          <div className="grid grid-cols-3 gap-2 sm:gap-2.5 pt-3 lg:pt-0 border-t lg:border-t-0 border-border/50 dark:border-zinc-800 shrink-0">
            {/* Tile 1: Role Authority */}
            <div className="p-2 sm:p-2.5 lg:px-3.5 lg:py-2.5 rounded-xl bg-muted/30 dark:bg-zinc-900/40 border border-border/60 dark:border-zinc-800/80 flex flex-col justify-between min-w-[85px] sm:min-w-[100px] lg:min-w-[125px]">
              <div className="flex items-center gap-1.5 text-muted-foreground">
                <Shield className="h-3.5 w-3.5 text-foreground shrink-0" />
                <span className="text-[10px] sm:text-[11px] font-medium truncate">
                  Role Authority
                </span>
              </div>
              <span className="text-xs sm:text-sm font-semibold text-foreground truncate mt-1">
                {profile.role === "super_admin" ? "Full Access" : "Client"}
              </span>
            </div>

            {/* Tile 2: 2FA Security */}
            <div className="p-2 sm:p-2.5 lg:px-3.5 lg:py-2.5 rounded-xl bg-muted/30 dark:bg-zinc-900/40 border border-border/60 dark:border-zinc-800/80 flex flex-col justify-between min-w-[85px] sm:min-w-[100px] lg:min-w-[125px]">
              <div className="flex items-center gap-1.5 text-muted-foreground">
                <ShieldCheck className="h-3.5 w-3.5 text-foreground shrink-0" />
                <span className="text-[10px] sm:text-[11px] font-medium truncate">
                  2FA Status
                </span>
              </div>
              <span
                className={`text-xs sm:text-sm font-semibold truncate mt-1 ${profile.is_totp_enabled
                  ? "text-emerald-600 dark:text-emerald-400 font-bold"
                  : "text-amber-600 dark:text-amber-400"
                  }`}
              >
                {profile.is_totp_enabled ? "Active" : "Disabled"}
              </span>
            </div>

            {/* Tile 3: Passkeys Enrolled */}
            <div className="p-2 sm:p-2.5 lg:px-3.5 lg:py-2.5 rounded-xl bg-muted/30 dark:bg-zinc-900/40 border border-border/60 dark:border-zinc-800/80 flex flex-col justify-between min-w-[85px] sm:min-w-[100px] lg:min-w-[125px]">
              <div className="flex items-center gap-1.5 text-muted-foreground">
                <Fingerprint className="h-3.5 w-3.5 text-foreground shrink-0" />
                <span className="text-[10px] sm:text-[11px] font-medium truncate">
                  Passkeys
                </span>
              </div>
              <span className="text-xs sm:text-sm font-semibold text-foreground truncate mt-1 font-mono">
                {passkeyCount} / {maxPasskeys}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ------------------------------------------------------------- */}
      {/* Cognitive Segmented Control (Pills)                           */}
      {/* ------------------------------------------------------------- */}
      <div className="flex items-center gap-1 p-1 bg-muted/60 dark:bg-zinc-900 rounded-xl border border-border/60 dark:border-zinc-800 w-full sm:w-auto overflow-x-auto">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-3.5 py-2 text-xs font-semibold rounded-lg transition-all duration-150 whitespace-nowrap min-h-[40px] sm:min-h-0 ${isActive
                ? "bg-card dark:bg-zinc-800 text-foreground shadow-xs border border-border/40 dark:border-zinc-700"
                : "text-muted-foreground hover:text-foreground hover:bg-card/40"
                }`}
            >
              <Icon className="h-3.5 w-3.5 shrink-0" />
              <span>{tab.label}</span>
              {tab.badge && (
                <span
                  className={`text-[10px] font-mono px-1.5 py-0.2 rounded-full ${isActive
                    ? "bg-zinc-950 text-white dark:bg-zinc-100 dark:text-zinc-950 font-semibold"
                    : "bg-muted text-muted-foreground"
                    }`}
                >
                  {tab.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* ------------------------------------------------------------- */}
      {/* Focused Active Tab Content (Eliminates Cognitive Overload)    */}
      {/* ------------------------------------------------------------- */}
      <div className="w-full">
        {/* Tab 1: General Details */}
        {activeTab === "general" && (
          <div className="animate-in fade-in-50 duration-150">
            <ProfileEditForm profile={profile} tab="general" />
          </div>
        )}

        {/* Tab 2: Password Change */}
        {activeTab === "password" && (
          <div className="animate-in fade-in-50 duration-150">
            <ProfileEditForm profile={profile} tab="password" />
          </div>
        )}

        {/* Tab 3: Security, 2FA, and Passkeys */}
        {activeTab === "security" && (
          <div className="space-y-5 animate-in fade-in-50 duration-150">
            {/* Authenticator 2FA Card */}
            <Card className="border-border/80 dark:border-zinc-800 bg-card dark:bg-zinc-950 shadow-xs rounded-2xl">
              <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-5">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-xl shrink-0 bg-muted/60 dark:bg-zinc-900 border border-border/60">
                    {profile.is_totp_enabled ? (
                      <ShieldCheck className="h-4.5 w-4.5 text-foreground shrink-0" />
                    ) : (
                      <ShieldAlert className="h-4.5 w-4.5 text-amber-500 shrink-0" />
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <CardTitle className="text-sm font-semibold">
                        Authenticator 2FA (TOTP)
                      </CardTitle>
                      <span
                        className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${profile.is_totp_enabled
                          ? "bg-zinc-950 text-white dark:bg-zinc-100 dark:text-zinc-950 border-zinc-900 dark:border-zinc-200"
                          : "bg-amber-50 dark:bg-amber-950/60 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800/60"
                          }`}
                      >
                        {profile.is_totp_enabled ? "Active" : "Disabled"}
                      </span>
                    </div>
                    <CardDescription className="text-xs text-muted-foreground mt-0.5">
                      Require a 6-digit code from Google Authenticator, Authy, or 1Password.
                    </CardDescription>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 shrink-0">
                  {!profile.is_totp_enabled && (
                    <span className="sm:hidden text-[11px] font-mono text-muted-foreground/80 text-center">
                      Desktop browser required to scan QR
                    </span>
                  )}
                  <Button
                    variant={profile.is_totp_enabled ? "outline" : "default"}
                    size="sm"
                    onClick={() => setIsTotpModalOpen(true)}
                    className="h-9 text-xs shrink-0"
                  >
                    {profile.is_totp_enabled ? "Manage / Disable 2FA" : "Enable Authenticator"}
                  </Button>
                </div>
              </CardHeader>
            </Card>

            {/* FIDO2 Passkeys Device Management */}
            <PasskeyDeviceList
              passkeys={profile.passkeys || []}
              maxPasskeys={maxPasskeys}
            />
          </div>
        )}
      </div>

      {/* Totp Setup Modal */}
      <TotpSetupModal
        open={isTotpModalOpen}
        isAlreadyEnabled={profile.is_totp_enabled}
        onClose={() => setIsTotpModalOpen(false)}
      />
    </div>
  );
}
